import React, { useState, useEffect } from 'react';
import { useUser } from './UserProvider';
import { useToast } from './Toast';
import Terms from './Terms';
import './Auth.css';
import { hashPassword, secureCleanup, validatePasswordStrengthClient } from '../utils/crypto';

const Auth = ({ onAuthSuccess, onClose }) => {
    const { login } = useUser();
    const { success, error: showError, warning } = useToast();
    const [isLogin, setIsLogin] = useState(true);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [passwordFeedback, setPasswordFeedback] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showResendVerification, setShowResendVerification] = useState(false);
    const [showResendModal, setShowResendModal] = useState(false);
    const [resendEmail, setResendEmail] = useState('');
    const [termsAgreed, setTermsAgreed] = useState(false);
    const [showTerms, setShowTerms] = useState(false);

    // 모달이 열릴 때 body에 클래스 추가
    useEffect(() => {
        document.body.classList.add('auth-modal-open');
        return () => {
            document.body.classList.remove('auth-modal-open');
        };
    }, []);

    const calculatePasswordStrength = (password) => {
        return validatePasswordStrengthClient(password);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // 비밀번호 강도 계산 (회원가입 모드에서만)
        if (name === 'password' && !isLogin) {
            const { strength, feedback } = calculatePasswordStrength(value);
            setPasswordStrength(strength);
            setPasswordFeedback(feedback);
        }
        
        // 입력 시 해당 필드의 에러 제거
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validatePasswordStrength = (password) => {
        const errors = [];
        
        if (password.length < 8) {
            errors.push('비밀번호는 최소 8자 이상이어야 합니다.');
        }
        
        if (!/[a-z]/.test(password)) {
            errors.push('소문자가 포함되어야 합니다.');
        }
        
        if (!/[0-9]/.test(password)) {
            errors.push('숫자가 포함되어야 합니다.');
        }
        
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            errors.push('특수문자가 포함되어야 합니다.');
        }
        
        if (/(.)\1{2,}/.test(password)) {
            errors.push('같은 문자가 3회 이상 연속될 수 없습니다.');
        }
        
        return errors;
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.username) {
            newErrors.username = '아이디를 입력해주세요.';
        } else if (formData.username.length < 3 || formData.username.length > 20) {
            newErrors.username = '아이디는 3-20자 사이여야 합니다.';
        } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
            newErrors.username = '아이디는 영문자, 숫자, 언더스코어만 사용할 수 있습니다.';
        }

        if (!isLogin) {
            if (!formData.email) {
                newErrors.email = '이메일을 입력해주세요.';
            } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
                newErrors.email = '유효한 이메일 주소를 입력해주세요.';
            }

            if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = '비밀번호가 일치하지 않습니다.';
            }

            // 회원가입 시 강화된 비밀번호 검증
            if (formData.password) {
                const passwordErrors = validatePasswordStrength(formData.password);
                if (passwordErrors.length > 0) {
                    newErrors.password = passwordErrors.join(' ');
                }
            }
        }

        if (!formData.password) {
            newErrors.password = '비밀번호를 입력해주세요.';
        } else if (formData.password.length < 6 || formData.password.length > 50) {
            newErrors.password = '비밀번호는 6-50자 사이여야 합니다.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setErrors({});

        // 비밀번호 보안을 위한 임시 변수
        const usernameCopy = formData.username;
        const emailCopy = formData.email;

        try {
            // 클라이언트 측에서 비밀번호 해싱 (회원가입과 로그인 모두 'login' 사용)
            const hashedPassword = hashPassword(formData.password, usernameCopy, 'login');
            
            // API 기본 URL 설정
            const endpoint = isLogin ? `/api/auth/login` : `/api/auth/register`;
            const payload = isLogin 
                ? { username: usernameCopy, hashedPassword: hashedPassword }
                : { 
                    username: usernameCopy, 
                    email: emailCopy, 
                    hashedPassword: hashedPassword 
                };

            // 메모리에서 비밀번호 즉시 제거
            setFormData(prev => ({
                ...prev,
                password: '',
                confirmPassword: ''
            }));

            // 추가 보안: 로컬 변수도 정리
            secureCleanup({ hashedPassword }, ['hashedPassword']);

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            console.log('📨 API 응답 수신:', { status: response.status, ok: response.ok });

            const data = await response.json();
            console.log('📨 API 응답 데이터:', data);

            if (response.ok) {
                if (isLogin) {
                    // 로그인 성공 - UserProvider의 login 함수 사용
                    login(data.user, data.token, data.adminToken || null);
                    success('로그인되었습니다.');
                    setShowResendVerification(false); // 성공 시 재전송 버튼 숨김
                    onAuthSuccess(data.user);
                } else {
                    // 회원가입 성공
                    success(data.message);
                    setIsLogin(true); // 로그인 폼으로 전환
                    setShowResendVerification(false); // 성공 시 재전송 버튼 숨김
                    setFormData({
                        username: '',
                        email: '',
                        password: '',
                        confirmPassword: ''
                    });
                }
            } else {
                // 오류 처리 - 이메일 미인증 오류인지 확인
                if (isLogin && (data.error.includes('이메일 인증이 필요합니다') || data.error.includes('이메일을 확인해주세요'))) {
                    setShowResendVerification(true);
                    warning('이메일 인증이 필요합니다. 이메일을 확인하거나 재전송 버튼을 이용해주세요.');
                } else {
                    setShowResendVerification(false);
                    showError(data.error);
                }
            }
        } catch (error) {
            console.error('Auth error:', error);
            showError('네트워크 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendVerification = () => {
        // 이미 이메일이 있으면 바로 사용, 없으면 모달 표시
        if (formData.email) {
            setResendEmail(formData.email);
        } else {
            setResendEmail('');
        }
        setShowResendModal(true);
    };

    const handleResendSubmit = async () => {
        if (!resendEmail) {
            showError('이메일 주소가 필요합니다.');
            return;
        }

        if (!/\S+@\S+\.\S+/.test(resendEmail)) {
            showError('유효한 이메일 주소를 입력해주세요.');
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`/api/auth/resend-verification`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: resendEmail }),
            });

            const data = await response.json();
            if (response.ok) {
                success('인증 이메일이 재전송되었습니다. 이메일을 확인해주세요.');
                setShowResendVerification(false); // 재전송 성공 시 버튼 숨김
                // 입력된 이메일을 폼에 저장
                if (!formData.email && resendEmail) {
                    setFormData(prev => ({ ...prev, email: resendEmail }));
                }
                // 3초 후 모달 자동 닫기
                setTimeout(() => {
                    setShowResendModal(false);
                }, 3000);
            } else {
                showError(data.error || '이메일 재전송에 실패했습니다.');
            }
        } catch (error) {
            showError('네트워크 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendModalClose = () => {
        setShowResendModal(false);
        setResendEmail('');
    };

    const handleForgotPassword = async () => {
        if (!formData.email) {
            setErrors({ email: '이메일을 입력해주세요.' });
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            const response = await fetch(`/api/auth/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: formData.email }),
            });

            const data = await response.json();
            
            if (response.ok) {
                success('비밀번호 재설정 이메일이 전송되었습니다. 이메일을 확인해주세요.');
                setShowForgotPassword(false);
            } else {
                // 미인증 사용자인 경우 특별 처리
                if (data.needsVerification) {
                    showError(`${data.error} 이메일 인증을 완료한 후 다시 시도해주세요.`);
                    // 이메일 재전송 버튼 표시를 위해 사용자명 저장
                    if (data.username) {
                        setFormData(prev => ({ ...prev, username: data.username }));
                        setShowResendVerification(true);
                    }
                } else {
                    showError(data.error || '비밀번호 재설정 이메일 전송에 실패했습니다.');
                }
            }
        } catch (error) {
            showError('네트워크 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleAuthMode = () => {
        setIsLogin(!isLogin);
        setShowForgotPassword(false);
        setShowResendVerification(false); // 모드 변경 시 재전송 버튼 숨김
        setShowPassword(false); // 비밀번호 표시 상태 초기화
        setShowConfirmPassword(false); // 비밀번호 확인 표시 상태 초기화
        setPasswordStrength(0); // 비밀번호 강도 초기화
        setPasswordFeedback([]); // 비밀번호 피드백 초기화
        setTermsAgreed(false); // 약관 동의 초기화
        setShowTerms(false); // 약관 펼치기 상태 초기화
        setFormData({
            username: '',
            email: '',
            password: '',
            confirmPassword: ''
        });
        setErrors({});
    };

    const toggleForgotPassword = () => {
        setShowForgotPassword(!showForgotPassword);
        setShowResendVerification(false); // 비밀번호 재설정 모드 시 재전송 버튼 숨김
        setShowPassword(false); // 비밀번호 표시 상태 초기화
        setShowConfirmPassword(false); // 비밀번호 확인 표시 상태 초기화
        setErrors({});
    };

    return (
        <>
        <div className="auth-overlay">
            <div className={`auth-modal ${!isLogin && !showForgotPassword ? 'auth-modal-signup' : ''}`}>
                <button className="auth-close" onClick={onClose}>×</button>
                
                <div className="auth-header">
                    <h2>
                        {showForgotPassword ? '비밀번호 재설정' : (isLogin ? '로그인' : '회원가입')}
                    </h2>
                </div>

                {showForgotPassword ? (
                    // 비밀번호 재설정 폼
                    <div className="forgot-password-form">
                        <p className="forgot-password-description">
                            가입 시 사용한 이메일 주소를 입력하시면, 비밀번호 재설정 링크를 보내드립니다.
                        </p>
                        
                        <div className="form-group">
                            <label htmlFor="email">이메일</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className={errors.email ? 'error' : ''}
                                placeholder="이메일을 입력해주세요"
                            />
                            {errors.email && <span className="error-text">{errors.email}</span>}
                        </div>

                        {errors.general && <div className="error-text general-error">{errors.general}</div>}

                        <button 
                            type="button" 
                            className="auth-button"
                            onClick={handleForgotPassword}
                            disabled={isLoading}
                        >
                            {isLoading ? '전송중...' : '재설정 이메일 전송'}
                        </button>

                        {/* 이메일 재전송 버튼 (미인증 사용자인 경우) */}
                        {showResendVerification && (
                            <button
                                type="button"
                                className="resend-verification-btn"
                                onClick={() => setShowResendModal(true)}
                            >
                                📧 이메일 인증 재전송
                            </button>
                        )}

                        <button 
                            type="button" 
                            className="auth-toggle forgot-back"
                            onClick={toggleForgotPassword}
                        >
                            ← 로그인으로 돌아가기
                        </button>
                    </div>
                ) : (
                    // 기존 로그인/회원가입 폼
                    <>
                    {/* 회원가입 시 이용약관 표시 */}
                    {!isLogin && (
                        <div className="terms-section">
                            <div className="terms-header">
                                <h3>이용약관</h3>
                                <button 
                                    type="button" 
                                    className="terms-toggle"
                                    onClick={() => setShowTerms(!showTerms)}
                                >
                                    {showTerms ? '접기' : '펼치기'}
                                </button>
                            </div>
                            
                            {showTerms && (
                                <Terms />
                            )}
                            
                            <div className="terms-agreement">
                                <label className="terms-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={termsAgreed}
                                        onChange={(e) => setTermsAgreed(e.target.checked)}
                                    />
                                    <span>위 이용약관에 동의합니다 (필수)</span>
                                </label>
                            </div>
                        </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="auth-form">
                    <div className="form-group">
                        <label htmlFor="username">아이디</label>
                        <input
                            type="text"
                            id="username"
                            name="username"
                            value={formData.username}
                            onChange={handleInputChange}
                            className={errors.username ? 'error' : ''}
                            placeholder="아이디를 입력해주세요"
                        />
                        {errors.username && <span className="error-text">{errors.username}</span>}
                    </div>

                    {!isLogin && (
                        <div className="form-group">
                            <label htmlFor="email">이메일</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className={errors.email ? 'error' : ''}
                                placeholder="이메일을 입력해주세요"
                            />
                            {errors.email && <span className="error-text">{errors.email}</span>}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="password">비밀번호</label>
                        <div className="password-input-container">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                className={errors.password ? 'error' : ''}
                                placeholder="비밀번호를 입력해주세요"
                                autoComplete={isLogin ? "current-password" : "new-password"}
                                spellCheck="false"
                                autoCorrect="off"
                                autoCapitalize="off"
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={togglePasswordVisibility}
                                aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                            >
                                {showPassword ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M2.45703 12C3.73128 7.94288 7.52159 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C20.2672 16.0571 16.4769 19 11.9992 19C7.52159 19 3.73128 16.0571 2.45703 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                        {errors.password && <span className="error-text">{errors.password}</span>}
                        
                        {/* 회원가입 시 비밀번호 강도 표시기 */}
                        {!isLogin && formData.password && (
                            <div className={`password-strength-container strength-${passwordStrength}`}>
                                <div className="password-strength-bar">
                                    <div 
                                        className={`password-strength-fill strength-${passwordStrength}`}
                                        style={{ width: `${(passwordStrength / 5) * 100}%` }}
                                    ></div>
                                </div>
                                <div className="password-strength-text">
                                    {passwordStrength === 5 ? '매우 강함' :
                                     passwordStrength === 4 ? '강함' :
                                     passwordStrength === 3 ? '보통' :
                                     passwordStrength === 2 ? '약함' : '매우 약함'}
                                </div>
                                {passwordFeedback.length > 0 && (
                                    <div className="password-feedback">
                                        <small>필요: {passwordFeedback.join(', ')}</small>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {!isLogin && (
                        <div className="form-group">
                            <label htmlFor="confirmPassword">비밀번호 확인</label>
                            <div className="password-input-container">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    className={errors.confirmPassword ? 'error' : ''}
                                    placeholder="비밀번호를 다시 입력해주세요"
                                    autoComplete="new-password"
                                    spellCheck="false"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={toggleConfirmPasswordVisibility}
                                    aria-label={showConfirmPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                                >
                                    {showConfirmPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.45703 12C3.73128 7.94288 7.52159 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C20.2672 16.0571 16.4769 19 11.9992 19C7.52159 19 3.73128 16.0571 2.45703 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                        </div>
                    )}

                    {errors.general && <div className="error-text general-error">{errors.general}</div>}
                    
                    {/* 이메일 미인증 오류 시에만 이메일 인증 재전송 버튼 표시 */}
                    {showResendVerification && (
                        <div className="verification-section">
                            <p className="verification-info">이메일 인증을 받지 못하셨나요?</p>
                            <button 
                                type="button" 
                                className="resend-verification"
                                onClick={handleResendVerification}
                                disabled={isLoading}
                            >
                                인증 이메일 재전송
                            </button>
                        </div>
                    )}

                    <button 
                        type="submit" 
                        className="auth-button"
                        disabled={isLoading || (!isLogin && !termsAgreed)}
                    >
                        {isLoading ? '처리중...' : (isLogin ? '로그인' : '회원가입')}
                    </button>
                </form>

                <div className="auth-footer">
                    <button 
                        type="button" 
                        className="auth-toggle"
                        onClick={toggleAuthMode}
                    >
                        {isLogin ? '계정이 없으신가요? 회원가입' : '이미 계정이 있으신가요? 로그인'}
                    </button>

                    {isLogin && (
                        <button 
                            type="button" 
                            className="forgot-password-link"
                            onClick={toggleForgotPassword}
                        >
                            비밀번호를 잊어버리셨나요?
                        </button>
                    )}
                </div>
                </>
                )}
            </div>
        </div>
        
        {/* 이메일 재전송 모달 */}
        {showResendModal && (
            <div className="auth-overlay">
                <div className="auth-modal resend-modal">
                    <button className="auth-close" onClick={handleResendModalClose}>×</button>
                    
                    <div className="auth-header">
                        <h2>이메일 재전송</h2>
                    </div>
                    
                    <div className="resend-modal-form">
                        <p className="resend-description">
                            인증 이메일을 재전송할 이메일 주소를 입력해주세요.
                        </p>
                        
                        <div className="form-group">
                            <label htmlFor="resendEmail">이메일</label>
                            <input
                                type="email"
                                id="resendEmail"
                                value={resendEmail}
                                onChange={(e) => setResendEmail(e.target.value)}
                                placeholder="이메일을 입력해주세요"
                                autoFocus
                            />
                        </div>
                        
                        <div className="resend-modal-actions">
                            <button 
                                type="button" 
                                className="auth-button"
                                onClick={handleResendSubmit}
                                disabled={isLoading || !resendEmail}
                            >
                                {isLoading ? '전송중...' : '이메일 재전송'}
                            </button>
                            
                            <button 
                                type="button" 
                                className="auth-toggle"
                                onClick={handleResendModalClose}
                            >
                                취소
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default Auth;